import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Search, Pagination, Dropdown, Modal } from '@carbon/react';
import { Add, View, Time, List, Grid as GridIcon, Document, Edit, TrashCan } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import { useVerifyReports } from '../hooks/useVerifyReports';
import { formatRelativeTime } from '../lib/timeUtils';
import SubscriptionGate from '../components/SubscriptionGate';
import ReportQRCode, { ReportQRCodePlaceholder } from '../components/ReportQRCode';

const VerifyListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { canUseVerifyReports } = useSubscription();
  const { reports, isLoading, error, refetch, deleteReport } = useVerifyReports();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Pagination state (only used for table view)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  // Delete confirmation handlers
  const handleDeleteClick = (report) => {
    setReportToDelete(report);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;

    setIsDeleting(true);
    try {
      await deleteReport(reportToDelete.id);
      setDeleteModalOpen(false);
      setReportToDelete(null);
    } catch (err) {
      console.error('Failed to delete report:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setReportToDelete(null);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredReports = reports.filter(report =>
    report.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations (only for table view)
  const totalItems = filteredReports.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedReports = viewMode === 'table' 
    ? filteredReports.slice(startIndex, endIndex)
    : filteredReports;

  // Reset to page 1 when search term changes or view mode changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode]);

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="verify-list-page">
        <div style={{ textAlign: 'center', color: textSecondary }}>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }} data-testid="verify-list-page">
        <div style={{ textAlign: 'center', color: '#da1e28' }}>
          <p>Error loading reports: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate 
      feature="verification reports and compliance documentation" 
      disabled={!canUseVerifyReports}
      customMessage="You need an active subscription to access verification reports and compliance documentation for your cold chain monitoring system."
    >
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="verify-list-page">
      {/* Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '16px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h1 style={{ color: textPrimary, fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 600, marginBottom: '4px' }}>Verify Reports</h1>
                  <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{filteredReports.length} of {reports.length} reports</p>
                </div>
                <Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/verify/new')} data-testid="add-report" size="md">
                  New Report
                </Button>
              </div>
              
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '100%' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search reports..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="md"
                    data-testid="search-reports"
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 0' }}>
        <Grid>
          {viewMode === 'cards' ? (
            // Card View
            filteredReports.length === 0 ? (
              <Column lg={16} md={8} sm={4}>
                <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px 16px', textAlign: 'center' }}>
                  <Document size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
                  <h3 style={{ color: textPrimary, marginBottom: '8px', fontSize: 'clamp(1rem, 4vw, 1.25rem)' }}>No Reports</h3>
                  <p style={{ color: textSecondary, marginBottom: '24px', fontSize: '0.875rem' }}>Create your first verify report to share sensor data</p>
                  <Button kind="primary" renderIcon={Add} onClick={() => navigate('/dashboard/verify/new')} size="md">
                    Create Report
                  </Button>
                </Tile>
              </Column>
            ) : (
              filteredReports.map((report) => (
                <Column lg={8} md={8} sm={4} key={report.id}>
                  <Tile
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      padding: '0',
                      marginBottom: '16px',
                      overflow: 'hidden',
                    }}
                    data-testid={`report-card-${report.id}`}
                  >
                    {/* Card Header */}
                    <div style={{ padding: '16px', borderBottom: `1px solid ${borderColor}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, margin: 0, marginBottom: '4px' }}>
                            {report.device_name || 'Unknown Device'}
                          </h3>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>
                            {report.start_date && report.end_date 
                              ? `${formatDate(report.start_date)} - ${formatDate(report.end_date)}`
                              : 'No date range'}
                          </p>
                        </div>
                        <Tag type="blue" size="sm">
                          {report.visit_count || 0} visits
                        </Tag>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Report ID</p>
                          <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {report.id?.substring(0, 8)}...
                          </p>
                        </div>
                        <div>
                          <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>Created</p>
                          <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>
                            {formatRelativeTime(report.created_at)}
                          </p>
                        </div>
                      </div>
                      {/* QR preview */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        <div
                          style={{
                            width: 88,
                            height: 88,
                            minWidth: 88,
                            borderRadius: 12,
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `2px solid ${isDark ? '#525252' : '#e0e0e0'}`,
                            boxShadow: isDark
                              ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                              : '0 4px 12px rgba(0, 0, 0, 0.1)',
                            flexShrink: 0,
                            padding: 4,
                          }}
                        >
                          {report.qr_code_url || report.public_url ? (
                            <ReportQRCode
                              publicUrl={report.public_url}
                              storedUrl={report.qr_code_url}
                              size={80}
                              alt="Report QR"
                            />
                          ) : (
                            <ReportQRCodePlaceholder size={80} />
                          )}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: textSecondary, flex: '1 1 180px', minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: textPrimary, marginBottom: '4px' }}>Scan to View Report</div>
                          <div style={{ wordBreak: 'break-word', lineHeight: '1.4' }}>Share this QR code with recipients to provide instant access to the report.</div>
                          {report.description && (
                            <div style={{ marginTop: '8px', color: textSecondary, wordBreak: 'break-word', padding: '6px 8px', backgroundColor: isDark ? '#393939' : '#f4f4f4', borderRadius: 4 }}>
                              <span style={{ fontWeight: 600 }}>Note: </span>
                              {report.description}
                            </div>
                          )}
                        </div>
                      </div>
                      {report.has_temperature && (
                        <Tag type="blue" size="sm" style={{ marginRight: '8px' }}>
                          Temperature
                        </Tag>
                      )}
                      {report.has_humidity && (
                        <Tag type="purple" size="sm">
                          Humidity
                        </Tag>
                      )}
                    </div>

                    {/* Card Footer - Actions */}
                    <div style={{ padding: '12px 16px', borderTop: `1px solid ${borderColor}`, display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={View}
                        onClick={() => navigate(`/dashboard/verify/${report.id}`)}
                        data-testid={`view-${report.id}`}
                      >
                        View
                      </Button>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={Edit}
                        onClick={() => navigate(`/dashboard/verify/${report.id}`)}
                        data-testid={`edit-${report.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        kind="danger--ghost"
                        size="sm"
                        renderIcon={TrashCan}
                        onClick={() => handleDeleteClick(report)}
                        data-testid={`delete-${report.id}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </Tile>
                </Column>
              ))
            )
          ) : (
            // Table View
            <Column lg={16} md={8} sm={4}>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -16px', padding: '0 16px' }}>
                <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                  <thead>
                    <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                      <th style={{ padding: '12px 8px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Device</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>QR Code</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Date Range</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Data Types</th>
                      <th style={{ padding: '12px 8px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Visits</th>
                      <th style={{ padding: '12px 8px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Created</th>
                      <th style={{ padding: '12px 8px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReports.map((report, idx) => (
                      <tr
                        key={report.id}
                        style={{
                          borderBottom: `1px solid ${borderColor}`,
                          backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                        }}
                        data-testid={`report-row-${report.id}`}
                      >
                        <td style={{ padding: '12px 8px' }}>
                          <p style={{ color: textPrimary, fontWeight: 500, margin: 0, fontSize: '0.875rem' }}>{report.device_name || 'Unknown Device'}</p>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              backgroundColor: '#ffffff',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `2px solid ${isDark ? '#525252' : '#e0e0e0'}`,
                              boxShadow: isDark
                                ? '0 2px 6px rgba(0, 0, 0, 0.3)'
                                : '0 2px 6px rgba(0, 0, 0, 0.08)',
                              padding: 2,
                            }}
                          >
                            {report.qr_code_url || report.public_url ? (
                              <ReportQRCode
                                publicUrl={report.public_url}
                                storedUrl={report.qr_code_url}
                                size={44}
                                alt="QR"
                              />
                            ) : (
                              <ReportQRCodePlaceholder size={44} />
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', color: textSecondary, fontSize: '0.875rem' }}>
                          {report.start_date && report.end_date
                            ? `${formatDate(report.start_date)} - ${formatDate(report.end_date)}`
                            : 'No date range'}
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {report.has_temperature && (
                              <Tag type="blue" size="sm">Temp</Tag>
                            )}
                            {report.has_humidity && (
                              <Tag type="purple" size="sm">Humidity</Tag>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 8px', textAlign: 'center', color: textPrimary, fontWeight: 600, fontSize: '0.875rem' }}>
                          {report.visit_count || 0}
                        </td>
                        <td style={{ padding: '12px 8px', color: textSecondary, fontSize: '0.875rem' }}>
                          {formatRelativeTime(report.created_at)}
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={View} iconDescription="View" onClick={() => navigate(`/dashboard/verify/${report.id}`)} data-testid={`view-table-${report.id}`} />
                            <Button kind="ghost" size="sm" hasIconOnly renderIcon={Edit} iconDescription="Edit" onClick={() => navigate(`/dashboard/verify/${report.id}`)} data-testid={`edit-table-${report.id}`} />
                            <Button kind="danger--ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Delete" onClick={() => handleDeleteClick(report)} data-testid={`delete-table-${report.id}`} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination - only show for table view */}
              {totalItems > 0 && (
                <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    backwardText="Previous"
                    forwardText="Next"
                    itemsPerPageText="Items per page:"
                    page={page}
                    pageNumberText="Page Number"
                    pageSize={pageSize}
                    pageSizes={[10, 20, 30, 40, 50]}
                    size="md"
                    totalItems={totalItems}
                    onChange={({ page, pageSize }) => {
                      setPage(page);
                      setPageSize(pageSize);
                    }}
                  />
                </div>
              )}
            </Column>
          )}
        </Grid>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        danger
        modalHeading="Delete Report"
        modalLabel="Confirm Action"
        primaryButtonText={isDeleting ? "Deleting..." : "Delete"}
        secondaryButtonText="Cancel"
        onRequestClose={handleDeleteCancel}
        onRequestSubmit={handleDeleteConfirm}
        primaryButtonDisabled={isDeleting}
        size="sm"
      >
        <p style={{ color: textPrimary }}>
          Are you sure you want to delete the report for{' '}
          <strong>{reportToDelete?.device_name || 'Unknown Device'}</strong>?
        </p>
        <p style={{ color: textSecondary, marginTop: '8px', fontSize: '0.875rem' }}>
          This action cannot be undone. All associated data will be permanently removed.
        </p>
      </Modal>
    </div>
    </SubscriptionGate>
  );
};

export default VerifyListPage;
